import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaHome, FaSearch, FaArrowLeft } from 'react-icons/fa';

const NotFound = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        {/* Animated 404 */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 20,
            duration: 0.8 
          }}
          className="mb-8"
        >
          <h1 className="text-[150px] md:text-[200px] font-bold bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 bg-clip-text text-transparent leading-none">
            404
          </h1>
        </motion.div>

        {/* Error Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4 mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Page Not Found
          </h2>
          <p className="text-gray-400 text-lg">
            Oops! The page you're looking for seems to have vanished into the digital void.
          </p>
        </motion.div>

        {/* Floating Particles */}
        <div className="relative h-32 mb-8">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-violet-500 rounded-full"
              initial={{ 
                x: Math.random() * 200 - 100, 
                y: 0,
                opacity: 0 
              }}
              animate={{
                y: [0, -100, -200],
                opacity: [0, 1, 0],
                x: Math.random() * 100 - 50
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2,
                ease: "easeOut"
              }}
              style={{
                left: `${20 + i * 15}%`
              }}
            />
          ))}
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg font-semibold flex items-center gap-3 shadow-lg hover:shadow-violet-500/50 transition-all"
            >
              <FaHome />
              Go Home
            </motion.button>
          </Link>

          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gray-800 text-white rounded-lg font-semibold flex items-center gap-3 hover:bg-gray-700 transition-all"
            >
              <FaArrowLeft />
              Go Back
            </motion.button>
          </Link>
        </motion.div>

        {/* Helpful Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-12 pt-8 border-t border-gray-800"
        >
          <p className="text-gray-500 text-sm mb-4">You might be looking for:</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/projects" 
              className="text-violet-400 hover:text-violet-300 transition-colors"
            >
              Projects
            </Link>
            <span className="text-gray-700">•</span>
            <Link 
              to="/about" 
              className="text-violet-400 hover:text-violet-300 transition-colors"
            >
              About
            </Link>
            <span className="text-gray-700">•</span>
            <Link 
              to="/contact" 
              className="text-violet-400 hover:text-violet-300 transition-colors"
            >
              Contact
            </Link>
          </div>
        </motion.div>

        {/* Fun Easter Egg */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8"
        >
          <p className="text-gray-600 text-xs">
            Error Code: LOST_IN_CYBERSPACE_42
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
